import type { TFunction } from 'i18next';
import { Bike, Check, Circle, MessageSquare, Phone, Wallet } from 'lucide-react';
import { useState } from 'react';

import { Button, Money, Spinner, StatusPill } from '@/shared/ui';
import { formatDate } from '@/shared/utils/date';

import { OrderCustomerProfileDialog } from './OrderCustomerProfileDialog';
import type { StaticOrderDetail } from '../lib/orderDetailStatic';
import { truncateAdminId } from '../lib/pendingPaymentContinuation';
import { isKnownOrderStatus } from '../lib/orderStatusPresentation';

export interface OrderDetailSidebarColumnProps {
  detail: StaticOrderDetail;
  t: TFunction<'orders'>;
}

function statusLabel(t: TFunction<'orders'>, status: string): string {
  const u = (status ?? '').trim().toUpperCase();
  if (isKnownOrderStatus(u)) return t(`status.${u}`);
  return status || '—';
}

function paymentStatusLabel(
  t: TFunction<'orders'>,
  paymentStatus: string,
  isPaid: boolean,
): string {
  if (paymentStatus === 'COMPLETED' || paymentStatus === 'PAID' || paymentStatus === 'SUCCESS') {
    return t('detail.payment.statusCompleted');
  }
  if (paymentStatus === 'PENDING_COLLECTION') return t('detail.payment.statusPendingCollection');
  if (paymentStatus === 'PENDING') return t('detail.payment.statusPending');
  if (paymentStatus === 'FAILED') return t('detail.payment.statusFailed');
  if (paymentStatus === 'EXPIRED') return t('detail.payment.statusExpired');
  return isPaid ? t('detail.payment.paid') : t('detail.payment.collectCod');
}

function paymentStatusTone(
  paymentStatus: string,
  isPaid: boolean,
): 'success' | 'warning' | 'danger' | 'neutral' {
  if (isPaid || paymentStatus === 'COMPLETED' || paymentStatus === 'PAID' || paymentStatus === 'SUCCESS') {
    return 'success';
  }
  if (paymentStatus === 'FAILED' || paymentStatus === 'EXPIRED') return 'danger';
  if (paymentStatus === 'PENDING') return 'warning';
  return 'warning';
}

function collectionModeLabel(t: TFunction<'orders'>, mode: string): string {
  if (mode === 'CASH') return t('detail.payment.collectionCash');
  if (mode === 'QR') return t('detail.payment.collectionQr');
  return mode || '—';
}

function collectionStatusLabel(t: TFunction<'orders'>, status: string): string {
  if (status === 'COLLECTED') return t('detail.payment.collectionCollected');
  if (status === 'SETTLED') return t('detail.payment.collectionSettled');
  return status || '—';
}

function pickingElapsedText(
  detail: StaticOrderDetail,
  t: TFunction<'orders'>,
): string {
  if (detail.slaPickingStartedAtIso) {
    return t('detail.lifecycle.pickingStartedAt', {
      time: formatDate(detail.slaPickingStartedAtIso, 'time'),
    });
  }
  if (detail.lifecycle.pickingElapsedLabelKey === 'detail.lifecycle.pickingInProgress') {
    return t('detail.lifecycle.pickingInProgress');
  }
  return t(detail.lifecycle.pickingElapsedLabelKey as 'detail.lifecycle.pickingElapsed');
}

export function OrderDetailSidebarColumn({ detail, t }: OrderDetailSidebarColumnProps) {
  const [profileOpen, setProfileOpen] = useState(false);
  const placedTime = formatDate(detail.placedAtIso, 'time');
  const deliveredEst = formatDate(detail.targetDeliveryAtIso, 'time');
  const confirmedTime = detail.confirmedAtIso
    ? formatDate(detail.confirmedAtIso, 'time')
    : undefined;

  const status = detail.status;
  const showConfirmedStep = Boolean(detail.confirmedAtIso);
  const isPicking =
    status === 'PICKING' ||
    status === 'PARTIALLY_FULFILLED' ||
    status === 'PACKED' ||
    status === 'DISPATCHED' ||
    status === 'DELIVERED' ||
    status === 'RETURNED';
  const isPickingActive = status === 'PICKING' || status === 'PARTIALLY_FULFILLED';
  const isPacked =
    status === 'PACKED' ||
    status === 'DISPATCHED' ||
    status === 'DELIVERED' ||
    status === 'RETURNED';
  const isDispatched =
    status === 'DISPATCHED' || status === 'DELIVERED' || status === 'RETURNED';
  const isDelivered = status === 'DELIVERED' || status === 'RETURNED';
  const isCancelled = status === 'CANCELLED';
  const isPaymentExpired = status === 'PAYMENT_EXPIRED';
  const isTerminalClosed = isCancelled || isPaymentExpired;
  const isReturned = status === 'RETURNED';
  const cancelledAtLabel = detail.cancelledAtIso ? formatDate(detail.cancelledAtIso, 'dateTime') : '';
  const returnedAtLabel = detail.returnedAtIso ? formatDate(detail.returnedAtIso, 'dateTime') : '';
  const paymentStatus = (detail.payment.paymentStatus ?? '').trim().toUpperCase();
  const collectionMode = (detail.payment.collectionMode ?? '').trim().toUpperCase();
  const collectionStatus = (detail.payment.collectionStatus ?? '').trim().toUpperCase();
  const collectedPaise = detail.payment.collectedAmountPaise;
  const history = detail.statusHistory ?? [];

  const note = detail.customer.deliveryNote.trim();
  const addressLabel = detail.customer.addressLabel?.trim();
  const logisticsStatus = detail.logistics.status;
  const agentName = detail.assignedDeliveryAgentName?.trim();

  return (
    <aside className="flex min-w-0 flex-col gap-8 lg:col-span-4">
      <section className="overflow-hidden rounded-2xl border border-border bg-surface shadow-sm">
        <div className="flex flex-col gap-6 p-6">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-xs font-bold uppercase tracking-widest text-fg">
              {t('detail.customer.heading')}
            </h2>
            <Button
              type="button"
              variant="link"
              className="h-auto gap-0 px-0 py-0 text-xs font-bold uppercase"
              onClick={() => setProfileOpen(true)}
            >
              {t('detail.customer.viewProfile')}
            </Button>
          </div>

          <OrderCustomerProfileDialog
            open={profileOpen}
            onOpenChange={setProfileOpen}
            phoneSearch={detail.customer.phoneSearch ?? ''}
            fallbackName={detail.customer.name}
          />

          <div className="flex items-center gap-4">
            <div
              className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-surface-muted text-lg font-bold text-primary ring-4 ring-surface ring-offset-0"
              aria-hidden
            >
              {detail.customer.initials}
            </div>
            <div className="min-w-0">
              <div className="text-lg font-bold text-fg">{detail.customer.name}</div>
              <div className="mt-1 flex items-center gap-1.5 text-sm text-fg-muted">
                <Phone className="h-3.5 w-3.5 shrink-0" aria-hidden />
                <span>{detail.customer.phoneDisplay}</span>
              </div>
            </div>
          </div>

          <div className="border-t border-border pt-6">
            <p className="text-[10px] font-bold uppercase tracking-widest text-fg-muted">
              {t('detail.customer.addressLabel')}
              {addressLabel ? (
                <span className="ml-2 font-semibold normal-case tracking-normal text-fg">
                  ({addressLabel})
                </span>
              ) : null}
            </p>
            <p className="mt-2 text-sm font-medium leading-relaxed text-fg">
              {detail.customer.addressLines.map((line, idx) => (
                <span key={`${idx}-${line}`} className="block">
                  {line}
                </span>
              ))}
            </p>
          </div>

          <div className="rounded-xl border border-info-subtle bg-info-subtle/40 p-4">
            <div className="flex items-center gap-2">
              <MessageSquare className="h-3.5 w-3.5 text-info-strong" aria-hidden />
              <p className="text-[10px] font-bold uppercase tracking-widest text-info-strong">
                {t('detail.customer.noteLabel')}
              </p>
            </div>
            <p className="mt-2 text-sm italic leading-relaxed text-fg-muted">
              {note || t('detail.customer.noteEmpty')}
            </p>
          </div>
        </div>
      </section>

      <section className="overflow-visible rounded-2xl border border-border bg-surface p-6 shadow-sm">
        <h2 className="text-xs font-bold uppercase tracking-widest text-fg">
          {t('detail.lifecycle.heading')}
        </h2>

        <div className="relative mt-8 overflow-visible pl-1">
          <div className="absolute bottom-2 left-[11px] top-2 w-0.5 bg-border" aria-hidden />

          <ol className="relative flex flex-col gap-8 overflow-visible">
            <li className="relative flex gap-4">
              <div
                className="relative z-[1] flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-success text-fg-on-primary ring-4 ring-success-subtle"
                aria-hidden
              >
                <Check className="h-3 w-3" strokeWidth={3} />
              </div>
              <div className="min-w-0 flex-1 pt-0.5">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="text-sm font-bold text-fg">{t('detail.lifecycle.placed')}</span>
                  <span className="text-[11px] font-medium text-fg-muted">{placedTime}</span>
                </div>
                <p className="mt-1 text-xs text-fg-muted">{t('detail.lifecycle.placedDesc')}</p>
              </div>
            </li>

            {showConfirmedStep && confirmedTime ? (
              <li className="relative flex gap-4">
                <div
                  className="relative z-[1] flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-success text-fg-on-primary ring-4 ring-success-subtle"
                  aria-hidden
                >
                  <Check className="h-3 w-3" strokeWidth={3} />
                </div>
                <div className="min-w-0 flex-1 pt-0.5">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <span className="text-sm font-bold text-fg">
                      {t('detail.lifecycle.confirmed')}
                    </span>
                    <span className="text-[11px] font-medium text-fg-muted">{confirmedTime}</span>
                  </div>
                  <p className="mt-1 text-xs text-fg-muted">
                    {t('detail.lifecycle.confirmedDesc')}
                  </p>
                </div>
              </li>
            ) : null}

            <li className="relative flex gap-4">
              <div
                className={`relative z-[1] flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${
                  isPacked
                    ? 'bg-success text-fg-on-primary ring-4 ring-success-subtle'
                    : isPickingActive && !isTerminalClosed
                      ? 'bg-primary ring-4 ring-primary-subtle'
                      : 'bg-surface'
                }`}
                aria-hidden
              >
                {isPacked ? (
                  <Check className="h-3 w-3" strokeWidth={3} />
                ) : (
                  <span className="h-1.5 w-1.5 rounded-full bg-surface" />
                )}
              </div>
              <div className="min-w-0 flex-1 pt-0.5">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span
                    className={`text-sm font-bold ${
                      isPickingActive ? 'text-primary' : 'text-fg'
                    }`}
                  >
                    {t('detail.lifecycle.picking')}
                  </span>
                  <span className="text-[11px] font-bold uppercase text-primary">
                    {isPickingActive ? t('detail.lifecycle.inProgress') : ''}
                  </span>
                </div>
                <p className="mt-1 text-xs text-fg-muted">
                  {isPicking || isPickingActive
                    ? pickingElapsedText(detail, t)
                    : t('detail.lifecycle.pickingWaiting')}
                </p>
              </div>
            </li>

            <li className="relative flex gap-4">
              <div
                className={`relative z-[1] flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${
                  isDispatched
                    ? 'bg-success text-fg-on-primary ring-4 ring-success-subtle'
                    : isPacked
                      ? 'bg-primary ring-4 ring-primary-subtle'
                      : 'bg-surface'
                }`}
                aria-hidden
              >
                {isDispatched ? (
                  <Check className="h-3 w-3" strokeWidth={3} />
                ) : (
                  <Circle strokeWidth={1} className="text-fg-muted" />
                )}
              </div>
              <div className={`min-w-0 flex-1 pt-0.5 ${isPacked || isDispatched ? '' : 'opacity-60'}`}>
                <p className="text-sm font-bold text-fg">{t('detail.lifecycle.dispatch')}</p>
                <p className="mt-1 text-xs text-fg-muted">
                  {isDispatched
                    ? t('detail.lifecycle.dispatchDesc')
                    : isPacked
                      ? t('detail.lifecycle.dispatchReady')
                      : ''}
                </p>
              </div>
            </li>

            <li className="relative flex gap-4">
              <div
                className={`relative z-[1] flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${
                  isDelivered
                    ? 'bg-success text-fg-on-primary ring-4 ring-success-subtle'
                    : 'bg-surface'
                }`}
                aria-hidden
              >
                {isDelivered ? (
                  <Check className="h-3 w-3" strokeWidth={3} />
                ) : (
                  <Circle strokeWidth={1} className="text-fg-muted" />
                )}
              </div>
              <div className={`min-w-0 flex-1 pt-0.5 ${isDelivered ? '' : 'opacity-60'}`}>
                <p className="text-sm font-bold text-fg">{t('detail.lifecycle.delivered')}</p>
                <p className="mt-1 text-xs text-fg-muted">
                  {isDelivered
                    ? formatDate(detail.deliveredAtIso || detail.targetDeliveryAtIso, 'time')
                    : t('detail.lifecycle.deliveredEst', { time: deliveredEst })}
                </p>
              </div>
            </li>

            {isCancelled ? (
              <li className="relative flex gap-4">
                <div
                  className="relative z-[1] flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-danger text-fg-on-primary ring-4 ring-danger-subtle"
                  aria-hidden
                >
                  <Check className="h-3 w-3" strokeWidth={3} />
                </div>
                <div className="min-w-0 flex-1 pt-0.5">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <p className="text-sm font-bold text-danger-strong">
                      {t('detail.lifecycle.cancelled')}
                    </p>
                    {cancelledAtLabel ? (
                      <span className="text-[11px] font-medium text-fg-muted">{cancelledAtLabel}</span>
                    ) : null}
                  </div>
                  {detail.cancellationReason ? (
                    <p className="mt-1 text-xs text-fg-muted">{detail.cancellationReason}</p>
                  ) : null}
                </div>
              </li>
            ) : null}

            {isPaymentExpired ? (
              <li className="relative flex gap-4">
                <div
                  className="relative z-[1] flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-danger text-fg-on-primary ring-4 ring-danger-subtle"
                  aria-hidden
                >
                  <Check className="h-3 w-3" strokeWidth={3} />
                </div>
                <div className="min-w-0 flex-1 pt-0.5">
                  <p className="text-sm font-bold text-danger-strong">
                    {t('detail.lifecycle.paymentExpired')}
                  </p>
                  <p className="mt-1 text-xs text-fg-muted">
                    {t('detail.lifecycle.paymentExpiredDesc')}
                  </p>
                </div>
              </li>
            ) : null}

            {isReturned ? (
              <li className="relative flex gap-4">
                <div
                  className="relative z-[1] flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-warning text-fg-on-primary ring-4 ring-warning-subtle"
                  aria-hidden
                >
                  <Check className="h-3 w-3" strokeWidth={3} />
                </div>
                <div className="min-w-0 flex-1 pt-0.5">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <p className="text-sm font-bold text-warning-strong">
                      {t('detail.lifecycle.returned')}
                    </p>
                    {returnedAtLabel ? (
                      <span className="text-[11px] font-medium text-fg-muted">{returnedAtLabel}</span>
                    ) : null}
                  </div>
                  {detail.returnReason ? (
                    <p className="mt-1 text-xs text-fg-muted">{detail.returnReason}</p>
                  ) : null}
                </div>
              </li>
            ) : null}
          </ol>
        </div>

        {history.length > 0 ? (
          <div className="mt-6 border-t border-border pt-4">
            <h3 className="text-[10px] font-bold uppercase tracking-widest text-fg-muted">
              {t('detail.lifecycle.historyHeading')}
            </h3>
            <ol className="mt-3 flex flex-col gap-3">
              {history.map((entry, idx) => (
                <li key={`${entry.createdAtIso}-${idx}`} className="text-xs text-fg-muted">
                  <span className="font-medium text-fg">
                    {entry.fromStatus
                      ? `${statusLabel(t, entry.fromStatus)} → ${statusLabel(t, entry.toStatus)}`
                      : statusLabel(t, entry.toStatus)}
                  </span>
                  <span className="ml-2">{formatDate(entry.createdAtIso, 'dateTime')}</span>
                  {entry.changedBy ? (
                    <p className="mt-0.5 font-mono text-[11px]" title={entry.changedBy}>
                      {t('detail.lifecycle.changedBy', {
                        adminId: truncateAdminId(entry.changedBy),
                      })}
                    </p>
                  ) : null}
                  {entry.notes ? <p className="mt-0.5 italic">{entry.notes}</p> : null}
                </li>
              ))}
            </ol>
          </div>
        ) : null}
      </section>

      <section className="rounded-2xl border border-border bg-surface p-6 shadow-sm">
        <h2 className="text-[10px] font-bold uppercase tracking-widest text-fg-muted">
          {t('detail.payment.heading')}
        </h2>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-surface-muted/60 p-4">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-surface text-primary shadow-sm ring-1 ring-border">
              <Wallet className="h-4 w-4" aria-hidden />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-bold text-fg">
                {detail.payment.methodLabelKey === 'detail.payment.methodOther' &&
                detail.payment.method
                  ? t('detail.payment.methodOther', { method: detail.payment.method })
                  : t(detail.payment.methodLabelKey as 'detail.payment.methodPrepaid')}
              </p>
              {detail.payment.transactionId ? (
                <p className="text-[11px] text-fg-muted">
                  {t('detail.payment.txn', { id: detail.payment.transactionId })}
                </p>
              ) : null}
            </div>
          </div>
          <StatusPill
            tone={paymentStatusTone(paymentStatus, detail.payment.isPaid)}
            icon={Check}
            className="text-[10px] uppercase"
          >
            {paymentStatusLabel(t, paymentStatus, detail.payment.isPaid)}
          </StatusPill>
        </div>

        {collectionMode || collectionStatus || collectedPaise != null ? (
          <div className="mt-4 grid gap-2 text-sm">
            {collectionMode ? (
              <div className="flex items-center justify-between gap-3">
                <span className="text-fg-muted">{t('detail.payment.collectionMode')}</span>
                <span className="font-medium text-fg">
                  {collectionModeLabel(t, collectionMode)}
                </span>
              </div>
            ) : null}
            {collectionStatus ? (
              <div className="flex items-center justify-between gap-3">
                <span className="text-fg-muted">{t('detail.payment.collectionStatus')}</span>
                <span className="font-medium text-fg">
                  {collectionStatusLabel(t, collectionStatus)}
                </span>
              </div>
            ) : null}
            {typeof collectedPaise === 'number' && Number.isInteger(collectedPaise) ? (
              <div className="flex items-center justify-between gap-3">
                <span className="text-fg-muted">{t('detail.payment.collectedAmount')}</span>
                <span className="font-medium tabular-nums text-fg">
                  <Money amount={collectedPaise} />
                </span>
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="mt-6 border-t border-border pt-6">
          <h3 className="text-[10px] font-bold uppercase tracking-widest text-fg-muted">
            {t('detail.logistics.heading')}
          </h3>
          <div className="mt-4">
            <div className="text-sm text-fg-muted">{t('detail.logistics.deliveryAgent')}</div>
            <div className="mt-1 text-sm font-semibold text-fg">
              {agentName || t('detail.logistics.unassigned')}
            </div>
          </div>
          {logisticsStatus !== 'none' ? (
            <div className="mt-4 flex items-center justify-between gap-3 rounded-xl border border-dashed border-primary/30 bg-primary-subtle/50 px-4 py-4">
              <div className="flex min-w-0 items-center gap-3">
                {logisticsStatus === 'assigning' ? (
                  <Spinner
                    className="text-primary"
                    size={20}
                    label={t('detail.logistics.spinnerLabel')}
                  />
                ) : (
                  <Bike className="h-4 w-4 shrink-0 text-primary" aria-hidden />
                )}
                <span className="text-sm font-semibold text-primary">
                  {t(detail.logistics.statusLabelKey as 'detail.logistics.assigning')}
                </span>
              </div>
              {logisticsStatus === 'assigning' ? (
                <Bike className="h-4 w-4 shrink-0 text-primary" aria-hidden />
              ) : null}
            </div>
          ) : null}
        </div>
      </section>
    </aside>
  );
}
